import type { Visit, VisitLabel, VisitLabels } from "./api";

/** Rule V-19: the note of a visit has 500 characters at most, as on the server. */
export const NOTE_MAX = 500;
export const CHIPS = 8;
export const TEXT_MAX = 40;
export const MATCHES_MAX = 8;

const active = (list: VisitLabels) => list.labels.filter((label) => label.active);

/** The labels of the chip row: the first active ones in the order of the organization. */
export const chipLabels = (list: VisitLabels): VisitLabel[] => active(list).slice(0, list.chips ?? CHIPS);

/** The active labels after the chips, which the "More labels" box finds. */
export const moreLabels = (list: VisitLabels): VisitLabel[] => active(list).slice(list.chips ?? CHIPS);

/** The labels of the "More labels" box that match the text and are not chosen, the labels that start with the text first. */
export function matchLabels(list: VisitLabels, chosen: readonly string[], query: string): VisitLabel[] {
  const text = query.trim().toLowerCase();
  const open = moreLabels(list).filter((label) => !chosen.includes(label.code));
  if (!text) return open.slice(0, MATCHES_MAX);
  const words = (label: VisitLabel) => label.text.toLowerCase().split(/[^a-z0-9]+/);
  const starts = open.filter((label) => words(label).some((word) => word.startsWith(text)));
  const inside = open.filter((label) => !starts.includes(label) && label.text.toLowerCase().includes(text));
  return [...starts, ...inside].slice(0, MATCHES_MAX);
}

/** The words when the "More labels" box finds nothing: the chip above or the outcome that has the text. */
export function noMatchText(list: VisitLabels, query: string, reasons: readonly string[] = []): string {
  const text = query.trim().toLowerCase();
  const has = (shown: string) => shown.toLowerCase().includes(text);
  const chip = chipLabels(list).find((label) => has(label.text));
  if (chip) return `“${chip.text}” is in the labels above.`;
  const reason = reasons.find(has);
  if (reason) return `“${reason}” is a reason of “Why nothing?”, not a label.`;
  return `No label matches “${query.trim()}”.`;
}

export const toggleLabel = (chosen: readonly string[], code: string): string[] =>
  chosen.includes(code) ? chosen.filter((found) => found !== code) : [...chosen, code];

/** The chosen labels that are not chips, as removable tags. */
export const tagLabels = (list: VisitLabels, chosen: readonly string[]): VisitLabel[] => {
  const chips = new Set(chipLabels(list).map((label) => label.code));
  return chosen.filter((code) => !chips.has(code)).map((code) => list.labels.find((label) => label.code === code) ?? { code, text: code, active: false });
};

export const noteCountText = (note: string) => `${note.length} / ${NOTE_MAX}`;

/** The note is never cut: a note over the limit gets this message and blocks the save. */
export const noteProblem = (note: string): string | null =>
  note.trim().length > NOTE_MAX ? `The note is ${note.trim().length} characters. The limit is ${NOTE_MAX}.` : null;

/** The labels of a saved visit in words; an older API sends only the codes, or nothing. */
export const visitLabelTexts = (visit: Pick<Visit, "labels" | "label_text">): string[] => visit.label_text ?? visit.labels ?? [];

/** The note of a saved visit that the driver typed. A Today visit keeps its stop cause in the note, which the outcome shows (SO-1065). */
export const visitNoteText = (visit: Pick<Visit, "kind" | "note" | "source">): string =>
  visit.kind === "skipped" || visit.source === "today" ? "" : (visit.note ?? "").trim();

/** One row of the label editor on Settings. A new row has no code until the server gives it one. */
export interface LabelRow {
  key: string;
  code: string | null;
  text: string;
  active: boolean;
}

export const labelRows = (list: VisitLabels): LabelRow[] => list.labels.map((label) => ({ key: label.code, code: label.code, text: label.text, active: label.active }));

/** Move the row at `from` one place up (-1) or down (+1). */
export function moveRow<T>(rows: readonly T[], from: number, step: -1 | 1): T[] {
  const to = from + step;
  if (to < 0 || to >= rows.length) return [...rows];
  const next = [...rows];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** The problem of one row of the editor, or null; `others` are the texts of the rows above it. */
export function labelRowProblem(row: LabelRow, others: readonly string[], max = TEXT_MAX): string | null {
  const text = row.text.trim();
  if (!text) return "Give the label a text, or remove the row.";
  if (text.length > max) return `A label has ${max} characters at most. This one has ${text.length}.`;
  if (others.includes(text.toLowerCase())) return "Another label has this text. Give each label a different text.";
  return null;
}

/** The first row with a problem and its text, or null. The server checks the same. */
export function labelRowsIssue(rows: readonly LabelRow[], max = TEXT_MAX): { key: string; text: string } | null {
  for (const [index, row] of rows.entries()) {
    const above = rows.slice(0, index).map((other) => other.text.trim().toLowerCase());
    const text = labelRowProblem(row, above, max);
    if (text) return { key: row.key, text };
  }
  return null;
}

/** What is wrong with the rows of the editor, or null. */
export const labelRowsProblem = (rows: readonly LabelRow[], max = TEXT_MAX): string | null => labelRowsIssue(rows, max)?.text ?? null;

export const labelChanges = (rows: readonly LabelRow[]) =>
  rows.map((row) => ({ ...(row.code ? { code: row.code } : {}), text: row.text.trim(), active: row.active }));
